import React, { useMemo } from 'react';
import { Box, Grid, Alert } from '@mui/material';
import {
  Support as SupportIcon,
  HelpOutline as HelpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { SupportMetrics, CHART_COLORS } from '../../types/dashboard.types';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';

export interface SupportMetricsSectionProps {
  data: SupportMetrics;
  loading?: boolean;
}

const SupportMetricsSection: React.FC<SupportMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const queryTypeData = useMemo(
    () =>
      data.queriesByType.map((item, index) => ({
        name: item.type,
        value: item.count,
        color: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.queriesByType]
  );

  const totalQueries = queryTypeData.reduce((sum, item) => sum + item.value, 0);
  const hasQueryData = queryTypeData.length > 0;

  return (
    <Box>
      <SectionHeader title="Support & Queries" icon={<SupportIcon />} />

      <Grid container spacing={3}>
        {/* Metric Cards */}
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Queries"
            value={totalQueries}
            icon={<HelpIcon />}
            color={CHART_COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Open Queries"
            value={data.totalOpenQueries}
            icon={<WarningIcon />}
            color={data.totalOpenQueries > 0 ? CHART_COLORS.warning : CHART_COLORS.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Resolution Rate"
            value={
              totalQueries > 0
                ? Math.round(((totalQueries - data.totalOpenQueries) / totalQueries) * 100)
                : 100
            }
            icon={<SupportIcon />}
            color={CHART_COLORS.success}
            format="percent"
            loading={loading}
          />
        </Grid>

        {/* Open Queries Alert */}
        {data.totalOpenQueries > 0 && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem',
                },
              }}
            >
              You have <strong>{data.totalOpenQueries}</strong> open support{' '}
              {data.totalOpenQueries === 1 ? 'query' : 'queries'} that need attention.
            </Alert>
          </Grid>
        )}

        {/* Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Queries by Type"
            subtitle="Distribution of support queries"
            height={280}
            loading={loading}
          >
            {!hasQueryData ? (
              <EmptyState
                message="No query data available"
                icon={<HelpIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={queryTypeData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {queryTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {queryTypeData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {item.name}: {item.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        {/* Query Type Breakdown */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Query Type Breakdown"
            subtitle="Detailed view of support queries"
            height={280}
            loading={loading}
          >
            {!hasQueryData ? (
              <EmptyState message="No query data available" height={250} />
            ) : (
              <Box sx={{ p: 1 }}>
                {queryTypeData.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      mb: 1,
                      bgcolor: '#f9fafb',
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: item.color,
                        }}
                      />
                      <Box
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#111827',
                        }}
                      >
                        {item.name}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                        }}
                      >
                        {item.value}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          minWidth: 40,
                        }}
                      >
                        {totalQueries > 0
                          ? `${((item.value / totalQueries) * 100).toFixed(0)}%`
                          : '0%'}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupportMetricsSection;
